import type { Series } from 'uplot';
import {
    Alert,
    CriticalSeverity,
    Event,
    InfoSeverity,
    MajorSeverity,
    MinorSeverity,
    WarningSeverity,
} from './constants';

// eslint-disable-next-line no-undef
type ToReactNodeFormatter = (value: number, xAxisResolution: number) => React.ReactNode;

type ToStringFormatter = (value: number, xAxisResolution: number) => string;
type ToNumberFormatter = (value: number, xAxisResolution: number) => number;

export type NamedFormatters = {
    rate: ToStringFormatter;
    durationMicros: ToStringFormatter;
    count: ToStringFormatter;
    bytes: ToStringFormatter;
    percentage: ToStringFormatter;
    kibBytes: ToStringFormatter;
};

export type NumberedFormatters = {
    rate: ToNumberFormatter;
    durationMicros: ToNumberFormatter;
    count: ToNumberFormatter;
    bytes: ToNumberFormatter;
    percentage: ToNumberFormatter;
    kibBytes: ToNumberFormatter;
};

export type GenericDataSet = {
    // Column seriesIds, first element need not be `_time`
    fields: [...Array<string>];

    // First column could be string or number for any categorical data, subsequent rows are
    // numeric data, with `null`s representing empty data for associated time.
    // columns: ((string | number)[] | number[])[];
    columns: [
        (string | number)[], // First row: string or number
        ...number[][], // Remaining rows: only numbers
    ];
};

export type DataSet = GenericDataSet & {
    // Column seriesIds, first element should be `_time`
    fields: ['_time', ...Array<string>];

    // First row should be time (milliseconds-since-epoch); subsequent rows are
    // numeric data, with `null`s representing empty data for associated time.
    columns: (number | null)[][];
};

export type LineDashStyle =
    | 'solid'
    | 'shortDash'
    | 'shortDot'
    | 'shortDashDot'
    | 'shortDashDotDot'
    | 'dot'
    | 'dash'
    | 'longDash'
    | 'dashDot'
    | 'longDashDot'
    | 'longDashDotDot';

export type StepPosition = 'before' | 'after' | 'off';

export type SeriesConfig = Record<
    string, // seriesId
    {
        /** type of presentation; default "line" */
        type?: 'line' | 'area' | 'points' | 'bar' | 'stacked-bar' | 'stacked-area';
        /** series label; defaults to field name */
        label?: string;
        /** series color; default random color from default color set */
        color?: string;
        /** scaleId to use for series */
        scale?: string;
        /** value formatter */
        valueFmt?: ToReactNodeFormatter | keyof NamedFormatters;
        /** series point diameter in CSS pixels; only applies when 'type' is 'points' */
        pointSize?: number;
        /**
         * Currently only used for series type 'line'.
         * Determines if line chart needs to show points on the series.
         * @deprecated Use markerDisplay instead
         */
        hidePoints?: boolean;
        /**
         * Controls marker display on series.
         * "off" - markers are hidden
         * "filled" - markers are filled circles
         * "outlined" - markers are outlined circles (hollow)
         */
        markerDisplay?: 'off' | 'filled' | 'outlined';
        /**
         * Dash style for line series.
         * Only applies to 'line' and 'area' type series.
         * Default is 'solid' (no dashes).
         */
        lineDashStyle?: LineDashStyle;
        /**
         * Step interpolation position for line/area series.
         * "off" disables stepped interpolation.
         * "before" draws vertical transitions before each x value.
         * "after" draws vertical transitions after each x value.
         */
        stepPosition?: StepPosition;
        /** if true, replaces null values with 0 for the series */
        replaceNullsWithZero?: boolean;
        /**
         * Overrides is an escape hatch to allow you to use whatever series config you want. Use sparingly.
         * It's useful in situations where we want to give more configurability to charts, like in a custom dashboard.
         */
        overrides?: Series;
    }
>;

export type ScaleConfig = Record<
    string, // scaleId
    {
        /** distribution of scaled values */
        type?: 'linear' | 'log';
        /** axis side to show scale; default "left" */
        side?: 'left' | 'right';
        /** label to show on axis */
        label?: string;
        /** show y-axis grid lines for scale; default true */
        showGrid?: boolean;
        /** width of y-axis; default 55 */
        width?: number;
        /** tick value formatter */
        tickFmt?: ToStringFormatter | keyof NamedFormatters;
        /** default seriesConfig for all series applied to this scale */
        seriesConfig?: Omit<SeriesConfig[keyof SeriesConfig], 'scale' | 'color'>;
        /** custom range of y-axis */
        range?:
            | [number | null, number | null]
            | ((u: any, dataMin: number, dataMax: number) => [number, number]);
        /** hide tick labels for this axis; default false */
        hideLabels?: boolean;
        /** rotation of axis labels in degrees; only applies to x-axis */
        labelRotation?: -90 | -45 | 0 | 45 | 90;
    }
>;

export type AxisRange = {
    min: number;
    max: number;
    dailyResolution?: boolean;
};

export type AnnotationType = typeof Alert | typeof Event;

export type AlertAnnotationSeverity =
    | typeof InfoSeverity
    | typeof WarningSeverity
    | typeof CriticalSeverity
    | typeof MajorSeverity
    | typeof MinorSeverity;

export type MarkAnnotation = {
    value: number;
    scale?: string; // default to "y"
    color?: string; // default to "defaultColor"
    style?: 'solid' | 'dashed' | 'dotted'; // defaults to `solid`
    width?: number;
    legendLabel?: string; // If you want the mark to show on the legend
};

export type TickInterval = '1s' | '1m' | '30m' | '1h' | '12h' | '1d' | '7d' | '1mo';

export type TimestampFormat = 'compact' | 'expanded';

export type LegendTruncation = 'ellipsisEnd' | 'ellipsisMiddle' | 'ellipsisStart' | 'ellipsisOff';

/**
 * Array of additional tooltip field values, one object per data point.
 * Each object maps field names to their values at that data point index.
 */
export type AdditionalTooltipValues = Record<string, string | number | null>[];

/**
 * Metadata messages contain the metadata from the output time series of your computation.
 */
/* eslint-disable camelcase */
export type StreamMetadataMessage = {
    type: 'metadata';
    channel: string;
    tsId: string;
    properties: {
        jobId: string;
        sf_organizationID: string;
        sf_streamLabel?: string;
        sf_key: string[];
        sf_metric: string;
        sf_originatingMetric: string;
        sf_resolutionMs: number;
        sf_type: string;
        sf_isPreQuantized: boolean;
        sf_tags?: string[];
    } & Record<string, string>;
};
/* eslint-enable camelcase */
